/**
 * Geolocation Service
 * 
 * Provides location detection capabilities using:
 * 1. IP-based geolocation (primary method)
 * 2. Browser Geolocation API (fallback)
 * 
 * Results are cached in sessionStorage to avoid repeated API calls.
 */

const CACHE_KEY = 'user_geolocation';
const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes

/**
 * Gets cached location data if available and not expired
 * @returns {Object|null} Cached location data or null
 */
const getCachedLocation = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      const now = Date.now();
      if (now - data.timestamp < CACHE_DURATION) {
        console.log('📍 [Geolocation] Using cached location:', data.location);
        return data.location;
      }
    }
  } catch (error) {
    console.error('📍 [Geolocation] Error reading cache:', error);
  }
  return null;
};

/**
 * Caches location data
 * @param {Object} location - Location data to cache
 */
const cacheLocation = (location) => {
  try {
    const data = {
      location,
      timestamp: Date.now()
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    console.log('📍 [Geolocation] Location cached:', location);
  } catch (error) {
    console.error('📍 [Geolocation] Error caching location:', error);
  }
};

/**
 * Gets user location using IP-based geolocation
 * @returns {Promise<Object|null>} Location data or null
 */
const getLocationFromIP = async () => {
  try {
    console.log('📍 [Geolocation] Attempting IP-based location detection...');
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`IP API returned ${response.status}`);
    }

    const data = await response.json();
    
    // Check if we got rate limited
    if (data.error) {
      console.warn('📍 [Geolocation] IP API error:', data.reason);
      return null;
    }

    const location = {
      country: data.country_name || data.country,
      countryCode: data.country_code,
      region: data.region,
      city: data.city,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      source: 'ip'
    };

    console.log('📍 [Geolocation] IP-based location detected:', location);
    return location;
  } catch (error) {
    console.error('📍 [Geolocation] IP-based location failed:', error);
    return null;
  }
};

/**
 * Gets user location using browser Geolocation API
 * @returns {Promise<Object|null>} Location data or null
 */
const getLocationFromBrowser = async () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('📍 [Geolocation] Browser Geolocation API not available');
      resolve(null);
      return;
    }

    console.log('📍 [Geolocation] Requesting browser geolocation...');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          console.log('📍 [Geolocation] Browser coords:', { latitude, longitude });

          // Try to reverse geocode using IP API
          const response = await fetch(
            `https://ipapi.co/${latitude},${longitude}/json/`
          );

          if (response.ok) {
            const data = await response.json();
            const location = {
              country: data.country_name || data.country,
              countryCode: data.country_code,
              region: data.region,
              city: data.city,
              latitude,
              longitude,
              timezone: data.timezone,
              source: 'browser'
            };
            console.log('📍 [Geolocation] Browser-based location:', location);
            resolve(location);
          } else {
            // Fallback: just return coordinates
            const location = {
              latitude,
              longitude,
              source: 'browser',
              country: null,
              countryCode: null
            };
            console.log('📍 [Geolocation] Browser location (coords only):', location);
            resolve(location);
          }
        } catch (error) {
          console.error('📍 [Geolocation] Browser reverse geocoding failed:', error);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source: 'browser',
            country: null,
            countryCode: null
          });
        }
      },
      (error) => {
        console.warn('📍 [Geolocation] Browser geolocation denied/failed:', error.message);
        resolve(null);
      },
      {
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

/**
 * Gets user's current location using available methods
 * @returns {Promise<Object|null>} Location object with country, region, coordinates, etc.
 */
export const getUserLocation = async () => {
  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  // Try IP-based geolocation first
  let location = await getLocationFromIP();
  
  // If IP-based failed, try browser geolocation
  if (!location) {
    console.log('📍 [Geolocation] IP method failed, trying browser API...');
    location = await getLocationFromBrowser();
  }

  // Cache the result if we got one
  if (location) {
    cacheLocation(location);
  } else {
    console.warn('📍 [Geolocation] All location detection methods failed');
  }

  return location;
};

/**
 * Regional groupings - maps regions to their member countries
 */
const REGION_COUNTRIES = {
  // Continental regions
  'europe': [
    'albania', 'andorra', 'armenia', 'austria', 'azerbaijan', 'belarus', 'belgium',
    'bosnia and herzegovina', 'bulgaria', 'croatia', 'cyprus', 'czech republic',
    'czechia', 'denmark', 'estonia', 'finland', 'france', 'georgia', 'germany',
    'greece', 'hungary', 'iceland', 'ireland', 'italy', 'kosovo', 'latvia',
    'liechtenstein', 'lithuania', 'luxembourg', 'malta', 'moldova', 'monaco',
    'montenegro', 'netherlands', 'north macedonia', 'norway', 'poland', 'portugal',
    'romania', 'russia', 'san marino', 'serbia', 'slovakia', 'slovenia', 'spain',
    'sweden', 'switzerland', 'ukraine', 'united kingdom', 'uk', 'vatican city'
  ],
  'asia': [
    'afghanistan', 'armenia', 'azerbaijan', 'bahrain', 'bangladesh', 'bhutan',
    'brunei', 'cambodia', 'china', 'cyprus', 'georgia', 'india', 'indonesia',
    'iran', 'iraq', 'israel', 'japan', 'jordan', 'kazakhstan', 'kuwait',
    'kyrgyzstan', 'laos', 'lebanon', 'malaysia', 'maldives', 'mongolia', 'myanmar',
    'nepal', 'north korea', 'oman', 'pakistan', 'palestine', 'philippines', 'qatar',
    'russia', 'saudi arabia', 'singapore', 'south korea', 'sri lanka', 'syria',
    'taiwan', 'tajikistan', 'thailand', 'timor-leste', 'turkey', 'turkmenistan',
    'united arab emirates', 'uae', 'uzbekistan', 'vietnam', 'yemen'
  ],
  'africa': [
    'algeria', 'angola', 'benin', 'botswana', 'burkina faso', 'burundi', 'cabo verde',
    'cameroon', 'central african republic', 'chad', 'comoros', 'congo', 'democratic republic of the congo',
    'djibouti', 'egypt', 'equatorial guinea', 'eritrea', 'eswatini', 'ethiopia',
    'gabon', 'gambia', 'ghana', 'guinea', 'guinea-bissau', 'ivory coast', 'kenya',
    'lesotho', 'liberia', 'libya', 'madagascar', 'malawi', 'mali', 'mauritania',
    'mauritius', 'morocco', 'mozambique', 'namibia', 'niger', 'nigeria', 'rwanda',
    'sao tome and principe', 'senegal', 'seychelles', 'sierra leone', 'somalia',
    'south africa', 'south sudan', 'sudan', 'tanzania', 'togo', 'tunisia', 'uganda',
    'zambia', 'zimbabwe'
  ],
  'north america': [
    'antigua and barbuda', 'bahamas', 'barbados', 'belize', 'canada', 'costa rica',
    'cuba', 'dominica', 'dominican republic', 'el salvador', 'grenada', 'guatemala',
    'haiti', 'honduras', 'jamaica', 'mexico', 'nicaragua', 'panama', 'saint kitts and nevis',
    'saint lucia', 'saint vincent and the grenadines', 'trinidad and tobago', 'united states',
    'usa', 'us'
  ],
  'south america': [
    'argentina', 'bolivia', 'brazil', 'chile', 'colombia', 'ecuador', 'guyana',
    'paraguay', 'peru', 'suriname', 'uruguay', 'venezuela'
  ],
  'latin america': [
    // Central America
    'belize', 'costa rica', 'el salvador', 'guatemala', 'honduras', 'nicaragua', 'panama',
    // South America
    'argentina', 'bolivia', 'brazil', 'chile', 'colombia', 'ecuador', 'guyana',
    'paraguay', 'peru', 'suriname', 'uruguay', 'venezuela',
    // Caribbean (Spanish/Portuguese speaking)
    'cuba', 'dominican republic', 'haiti', 'puerto rico',
    // Mexico
    'mexico'
  ],
  'central america': [
    'belize', 'costa rica', 'el salvador', 'guatemala', 'honduras', 'nicaragua', 'panama'
  ],
  'caribbean': [
    'antigua and barbuda', 'bahamas', 'barbados', 'cuba', 'dominica', 'dominican republic',
    'grenada', 'haiti', 'jamaica', 'saint kitts and nevis', 'saint lucia',
    'saint vincent and the grenadines', 'trinidad and tobago', 'puerto rico'
  ],
  'oceania': [
    'australia', 'fiji', 'kiribati', 'marshall islands', 'micronesia', 'nauru',
    'new zealand', 'palau', 'papua new guinea', 'samoa', 'solomon islands', 'tonga',
    'tuvalu', 'vanuatu'
  ],
  'middle east': [
    'bahrain', 'egypt', 'iran', 'iraq', 'israel', 'jordan', 'kuwait', 'lebanon',
    'oman', 'palestine', 'qatar', 'saudi arabia', 'syria', 'turkey', 
    'united arab emirates', 'uae', 'yemen'
  ],
  // Common aliases
  'eu': [
    'austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech republic', 'czechia',
    'denmark', 'estonia', 'finland', 'france', 'germany', 'greece', 'hungary',
    'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta', 'netherlands',
    'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden'
  ],
  'european union': [
    'austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech republic', 'czechia',
    'denmark', 'estonia', 'finland', 'france', 'germany', 'greece', 'hungary',
    'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta', 'netherlands',
    'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden'
  ]
};

/**
 * Normalizes region string for comparison
 * @param {string} region - Region string (e.g., "🇺🇸 USA Only", "USA", "United States")
 * @returns {string} Normalized region string
 */
const normalizeRegion = (region) => {
  if (!region) return '';
  
  // Remove ALL emojis using comprehensive regex
  const cleaned = region
    .replace(/[\u{1F000}-\u{1F9FF}]/gu, '') // All emoji blocks
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Miscellaneous Symbols and Pictographs
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport and Map Symbols
    .replace(/[\u{1F700}-\u{1F77F}]/gu, '') // Alchemical Symbols
    .replace(/[\u{1F780}-\u{1F7FF}]/gu, '') // Geometric Shapes Extended
    .replace(/[\u{1F800}-\u{1F8FF}]/gu, '') // Supplemental Arrows-C
    .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Supplemental Symbols and Pictographs
    .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '') // Chess Symbols
    .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Symbols and Pictographs Extended-A
    .replace(/[\u{2600}-\u{26FF}]/gu, '')   // Miscellaneous Symbols
    .replace(/[\u{2700}-\u{27BF}]/gu, '')   // Dingbats
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')   // Variation Selectors
    .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '') // Flag emojis
    .trim()
    .toLowerCase();

  // Map common variations
  const mappings = {
    'usa': 'united states',
    'us': 'united states',
    'uk': 'united kingdom',
    'uae': 'united arab emirates',
    'worldwide': 'worldwide',
    'global': 'worldwide',
    'latam': 'latin america',
    'latinamerica': 'latin america'
  };

  // Check if the cleaned string contains any mapped key
  for (const [key, value] of Object.entries(mappings)) {
    if (cleaned.includes(key)) {
      return value;
    }
  }

  return cleaned;
};

/**
 * Checks if a country belongs to a specific region
 * @param {string} country - Country name (normalized)
 * @param {string} region - Region name (normalized)
 * @returns {boolean} True if country is in region
 */
const isCountryInRegion = (country, region) => {
  if (!country || !region) return false;

  // Direct country match
  if (country === region) return true;

  // Check if region is a defined regional grouping
  const regionCountries = REGION_COUNTRIES[region];
  if (regionCountries) {
    return regionCountries.some(c => {
      // Check if country matches or is contained in the region country name
      return c === country || c.includes(country) || country.includes(c);
    });
  }

  return false;
};

/**
 * Checks if user's location matches any of the allowed regions
 * @param {Object} userLocation - User's location object
 * @param {string} allowedRegions - Comma-separated string of allowed regions (e.g., "🇺🇸 USA Only, 🇨🇦 Canada")
 * @returns {boolean} True if user is in allowed region
 */
export const isUserInAllowedRegion = (userLocation, allowedRegions) => {
  if (!userLocation) {
    console.warn('📍 [Geolocation] No user location available for comparison');
    return true; // Default to allowing if we can't determine location
  }

  if (!allowedRegions || allowedRegions.trim() === '') {
    console.log('📍 [Geolocation] No region restrictions set');
    return true;
  }

  // Check for worldwide/global
  const normalizedAllowed = normalizeRegion(allowedRegions);
  if (normalizedAllowed.includes('worldwide') || normalizedAllowed.includes('global')) {
    console.log('📍 [Geolocation] Job is available worldwide');
    return true;
  }

  // Split allowed regions (might be comma-separated)
  const allowedList = allowedRegions
    .split(/[,;]/)
    .map(r => normalizeRegion(r))
    .filter(r => r.length > 0);

  console.log('📍 [Geolocation] Allowed regions:', allowedList);

  // Get user's country and region info
  const userCountry = normalizeRegion(userLocation.country || '');
  const userCountryCode = (userLocation.countryCode || '').toLowerCase();
  const userRegion = normalizeRegion(userLocation.region || '');

  console.log('📍 [Geolocation] User location:', {
    country: userCountry,
    countryCode: userCountryCode,
    region: userRegion
  });

  // Check if user's location matches any allowed region
  const isAllowed = allowedList.some(allowed => {
    // Direct country name match
    if (userCountry && allowed.includes(userCountry)) {
      console.log(`📍 [Geolocation] ✓ Direct country match: "${userCountry}" in "${allowed}"`);
      return true;
    }
    
    // Check if user's country is mentioned in allowed regions
    if (userCountry && userCountry.includes(allowed)) {
      console.log(`📍 [Geolocation] ✓ Country contains region: "${userCountry}" contains "${allowed}"`);
      return true;
    }

    // Check country code
    if (userCountryCode && allowed.includes(userCountryCode)) {
      console.log(`📍 [Geolocation] ✓ Country code match: "${userCountryCode}" in "${allowed}"`);
      return true;
    }

    // Check if user's country belongs to a regional grouping (e.g., Argentina in Latin America)
    if (userCountry && isCountryInRegion(userCountry, allowed)) {
      console.log(`📍 [Geolocation] ✓ Regional grouping match: "${userCountry}" is in "${allowed}"`);
      return true;
    }

    // Check user's regional subdivision (e.g., state/province matches)
    if (userRegion && allowed.includes(userRegion)) {
      console.log(`📍 [Geolocation] ✓ Regional subdivision match: "${userRegion}" in "${allowed}"`);
      return true;
    }

    return false;
  });

  console.log(`📍 [Geolocation] User ${isAllowed ? 'IS ✓' : 'IS NOT ✗'} in allowed region`);
  return isAllowed;
};

/**
 * Clears cached location data
 */
export const clearLocationCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    console.log('📍 [Geolocation] Cache cleared');
  } catch (error) {
    console.error('📍 [Geolocation] Error clearing cache:', error);
  }
};

